import { render } from "preact";
import { useState, useMemo, useCallback } from "preact/hooks";
import { registerAction } from "../../actionRegistry";
import { api } from "../../api";
import { MOCKUP_BACKEND_URL } from "../../lib/constants";
import { modal } from "../../ui/modal";
import { toastSuccess, toastError } from "../../ui/messages";

interface MockupItem {
  id: string;
  name: string;
  keywords: string | null;
  aspect: string;
  previewImg: number;
  productType: string;
  yourInterior: boolean;
  isSelected: boolean;
  favState: boolean;
}

function updateExternalCounts(count: number): void {
  const countEl = document.getElementById("mockup-count");
  const sEl = document.getElementById("mockup-s");
  const genEl = document.getElementById("mockup-generate");
  if (countEl) countEl.textContent = String(count);
  if (sEl) sEl.textContent = count === 1 ? "" : "s";
  if (genEl) genEl.style.opacity = count > 0 ? "1" : "0.2";
}

function MockupCard({
  mockup,
  selected,
  fav,
  onToggle,
  onFav,
  onPreview,
}: {
  mockup: MockupItem;
  selected: boolean;
  fav: boolean;
  onToggle: () => void;
  onFav: () => void;
  onPreview: () => void;
}) {
  const borderColor = selected ? "#4AA14F" : "#fff";
  const icon = selected ? <i class="fas fa-check-square" /> : <i class="far fa-square" />;
  const iconClass = selected ? "interior-select-icon-on" : "interior-select-icon-off";
  const hclass = fav ? "mockup-heart-on" : "mockup-heart-off";
  const hicon = fav ? <i class="fas fa-heart" /> : <i class="far fa-heart" />;
  const yourInteriorClass = mockup.yourInterior ? " interiorpreview-your" : "";
  const preview = `${MOCKUP_BACKEND_URL}?p=${mockup.previewImg}&i=${mockup.id}`;

  return (
    <div
      style={`height: 25vw; width: 25vw; border: 5px solid ${borderColor}; margin: 2vw 2vw 0vw 0vw; background: #f1f1f1; display: inline-block; border-radius: 20px;`}
      class={`interiorpreview interiorpreview-new interiorpreview-all interiorpreview-${mockup.productType}${yourInteriorClass}`}
    >
      <table cellPadding="0" cellSpacing="0" width="100%" style="height: 100%">
        <tr>
          <td>
            <div style="text-align: center;">
              <div style="font-size: 0.8em; margin: 0px 0px 10px 0px; font-weight: 800;">
                {mockup.name}
              </div>
              <img
                src={preview}
                style="border-radius: 5px; max-width: 19vw; max-height: 19vw; cursor: pointer;"
                onClick={onToggle}
              />
              <div style="margin: 10px 0px 0px 0px;">
                <span
                  class={hclass}
                  style="cursor: pointer; float: left; position: relative; left: 1vw; font-size: 1.4em;"
                  onClick={onFav}
                >
                  {hicon}
                </span>
                <span class={iconClass} style="cursor: pointer;" onClick={onToggle}>
                  {icon}
                </span>
                <span
                  style="border: 1px solid #555; cursor: pointer; color: #555; padding: 5px 10px 5px 10px; border-radius: 10px; position: relative; bottom: 3px; margin: 0px 0px 0px 10px;"
                  onClick={onPreview}
                >
                  <i class="fas fa-search-plus" />
                </span>
              </div>
            </div>
          </td>
        </tr>
      </table>
    </div>
  );
}

type Filters = Record<string, string>;

function MockupSelectorGrid({ initialMockups }: { initialMockups: MockupItem[] }) {
  const [selection, setSelection] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const m of initialMockups) {
      if (m.isSelected) map[m.id] = true;
    }
    return map;
  });

  const [favs, setFavs] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const m of initialMockups) {
      if (m.favState) map[m.id] = true;
    }
    return map;
  });

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Filters>({});
  const [selectedOnly, setSelectedOnly] = useState(false);

  const selectedCount = useMemo(() => Object.values(selection).filter(Boolean).length, [selection]);

  // Keep external elements in sync
  updateExternalCounts(selectedCount);

  const toggleSelect = useCallback((id: string) => {
    setSelection((prev) => {
      const next = { ...prev };
      next[id] = !prev[id];
      return next;
    });
  }, []);

  const toggleFav = useCallback(async (id: string) => {
    try {
      const response = await api.mockups.toggleFav({ id });
      setFavs((prev) => ({ ...prev, [id]: response.state === "on" }));
      toastSuccess(
        response.state === "on"
          ? "Mockup design added to favorites"
          : "Mockup design removed from favorites"
      );
    } catch (err: unknown) {
      toastError((err as Error).message);
    }
  }, []);

  const deselectAll = useCallback(() => {
    setSelection({});
  }, []);

  const filteredMockups = useMemo(() => {
    return initialMockups.filter((m) => {
      const kw = (m.keywords ?? "").toLowerCase();
      const name = m.name.toLowerCase();

      // Category filters
      for (const val of Object.values(filters)) {
        if (val && !kw.includes(val.toLowerCase())) return false;
      }

      // Selected only
      if (selectedOnly && !selection[m.id]) return false;

      // Search
      if (search) {
        const q = search.toLowerCase();
        if (!kw.includes(q) && !name.includes(q)) return false;
      }

      return true;
    });
  }, [initialMockups, filters, selectedOnly, search, selection]);

  // Expose deselect + filter + selection functions for legacy callers via action registry
  registerAction("__mockupDeselectAll", deselectAll);
  registerAction("__mockupGetSelected", () => {
    return Object.entries(selection)
      .filter(([, v]) => v)
      .map(([id]) => id);
  });
  registerAction("__mockupSetSearch", (val: string) => {
    setSearch(val);
  });
  registerAction("__mockupSetFilter", (key: string, val: string) => {
    setFilters((prev) => ({ ...prev, [key]: val }));
  });
  registerAction("__mockupSetSelectedOnly", (val: boolean) => {
    setSelectedOnly(val);
  });

  return (
    <div style="text-align: center;">
      {filteredMockups.map((m) => (
        <MockupCard
          key={m.id}
          mockup={m}
          selected={!!selection[m.id]}
          fav={!!favs[m.id]}
          onToggle={() => {
            toggleSelect(m.id);
          }}
          onFav={() => void toggleFav(m.id)}
          onPreview={() => {
            modal(`showinterior:${m.id}:${m.id}:`, "large");
          }}
        />
      ))}
    </div>
  );
}

export async function mountMockupSelectorGrid(): Promise<void> {
  const container = document.getElementById("mockup-target");
  if (!container) return;

  try {
    const response = await api.mockups.getAll();
    const mockups: MockupItem[] = response.mockups ?? [];
    render(<MockupSelectorGrid initialMockups={mockups} />, container);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}
